/**
 * What the app does when the session changes (start.tsx wires it):
 *
 * - **the identity changed** (sign-out, or a sign-in as someone else over an
 *   existing session, or another tab doing either): the query cache is
 *   cleared, so user B never sees user A's rundowns or `/me` while a refetch
 *   is in flight;
 * - **the session ended**: the router re-runs its guard, which sends the user
 *   to /login.
 *
 * A refresh of the same user's tokens changes neither.
 */
import type { Session, SessionStore } from '../auth/session.js';

function identityOf(session: Session | null): string | null {
  return session === null ? null : `${session.tenantId ?? ''}/${session.userId}`;
}

export function bindSessionEffects(
  sessions: Pick<SessionStore, 'getSession' | 'subscribe'>,
  effects: { readonly clearCache: () => void; readonly revalidate: () => void },
): () => void {
  let identity = identityOf(sessions.getSession());
  return sessions.subscribe(() => {
    const session = sessions.getSession();
    const next = identityOf(session);
    if (next !== identity) effects.clearCache();
    identity = next;
    if (session === null) effects.revalidate();
  });
}

/**
 * start.tsx's wiring: the effects above onto the app's query cache and
 * router, and another tab's storage writes onto the session store.
 */
export function connectSession(
  sessions: Pick<SessionStore, 'getSession' | 'subscribe' | 'reload'>,
  queryClient: { readonly clear: () => void },
  router: { readonly invalidate: () => Promise<unknown> },
  onStorageChange: (listener: () => void) => void,
): void {
  bindSessionEffects(sessions, {
    clearCache: () => queryClient.clear(),
    revalidate: () => {
      void router.invalidate();
    },
  });
  // Another tab signed in, out or refreshed: follow it.
  onStorageChange(() => sessions.reload());
}
